import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, LogOut, RefreshCw, Inbox, ArrowLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "eh_admin_token";

const FIELD =
  "w-full rounded-none border border-line bg-transparent px-4 py-3.5 text-sm text-white placeholder:text-smoke/60 focus:outline-none focus:ring-2 focus:ring-volt focus:border-volt transition-colors duration-300";

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [enquiries, setEnquiries] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setEnquiries(null);
  }, []);

  const fetchEnquiries = useCallback(async () => {
    setRefreshing(true);
    try {
      const { data } = await axios.get(`${API}/enquiries`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEnquiries(data);
    } catch (e) {
      if (e.response?.status === 401) {
        logout();
      } else {
        toast.error("Could not load enquiries.");
        setEnquiries([]);
      }
    } finally {
      setRefreshing(false);
    }
  }, [token, logout]);

  useEffect(() => {
    if (token) fetchEnquiries();
  }, [token, fetchEnquiries]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      toast.success(`Welcome back, ${data.user.name}.`);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-coal px-6 font-sans text-white">
        <form
          onSubmit={login}
          data-testid="admin-login-form"
          className="w-full max-w-md border border-line bg-ash/50 p-8 md:p-10"
        >
          <img src="/assets/logo.png" alt="ElectroHaul" className="h-10 w-auto" />
          <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.25em] text-volt">
            Admin Access
          </p>
          <h1 className="mt-3 font-display text-2xl font-medium tracking-tight">
            Enquiry Dashboard
          </h1>
          <div className="mt-8 space-y-5">
            <div>
              <label htmlFor="a-email" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-smoke">
                Email
              </label>
              <input
                id="a-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@electrohaul.in"
                data-testid="admin-email-input"
                className={FIELD}
              />
            </div>
            <div>
              <label htmlFor="a-pass" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-smoke">
                Password
              </label>
              <input
                id="a-pass"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••••••"
                data-testid="admin-password-input"
                className={FIELD}
              />
            </div>
          </div>
          {error && (
            <p data-testid="admin-login-error" className="mt-4 font-mono text-xs text-red-400">
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-button"
            className="mt-8 flex w-full items-center justify-center gap-2 bg-volt px-6 py-4 font-mono text-xs font-bold uppercase tracking-[0.25em] text-black transition-opacity disabled:opacity-60"
          >
            {loading ? (
              <>
                Verifying <Loader2 className="h-4 w-4 animate-spin" />
              </>
            ) : (
              "Sign In"
            )}
          </button>
          <a
            href="/"
            data-testid="admin-back-link"
            className="mt-6 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-smoke transition-colors hover:text-volt"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to site
          </a>
        </form>
      </div>
    );
  }

  const calcCount = (enquiries || []).filter((q) => q.source === "calculator").length;

  return (
    <div className="min-h-screen bg-coal font-sans text-white">
      <header className="sticky top-0 z-40 border-b border-line bg-black/70 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[1440px] items-center justify-between px-6 py-4 md:px-12">
          <div className="flex items-center gap-4">
            <img src="/assets/logo.png" alt="ElectroHaul" className="h-9 w-auto" />
            <span className="hidden font-mono text-[10px] uppercase tracking-[0.25em] text-smoke sm:block">
              Enquiry Dashboard
            </span>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={fetchEnquiries}
              data-testid="admin-refresh-button"
              className="flex items-center gap-2 border border-line px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-smoke transition-colors hover:border-volt hover:text-volt"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
              Refresh
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout-button"
              className="flex items-center gap-2 border border-line px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-smoke transition-colors hover:border-red-400 hover:text-red-400"
            >
              <LogOut className="h-3.5 w-3.5" />
              Logout
            </button>
          </div>
        </div>
      </header>

      <main data-testid="admin-dashboard" className="mx-auto max-w-[1440px] px-6 py-12 md:px-12">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-volt">Inbox</p>
            <h1 className="mt-2 font-display text-3xl font-medium tracking-tight sm:text-4xl">
              Stored Enquiries
            </h1>
          </div>
          <div className="flex gap-8 font-mono text-xs uppercase tracking-[0.2em] text-smoke">
            <p>
              <span className="text-2xl font-bold text-white" data-testid="admin-total-count">
                {enquiries ? enquiries.length : "—"}
              </span>{" "}
              Total
            </p>
            <p>
              <span className="text-2xl font-bold text-volt" data-testid="admin-calc-count">
                {enquiries ? calcCount : "—"}
              </span>{" "}
              Calculator Leads
            </p>
          </div>
        </div>

        <div className="mt-10 border-l border-t border-line">
          {enquiries === null ? (
            <div className="flex items-center gap-3 border-b border-r border-line p-10 font-mono text-xs uppercase tracking-[0.2em] text-smoke">
              <Loader2 className="h-4 w-4 animate-spin text-volt" /> Loading enquiries
            </div>
          ) : enquiries.length === 0 ? (
            <div className="flex items-center gap-3 border-b border-r border-line p-10 font-mono text-xs uppercase tracking-[0.2em] text-smoke">
              <Inbox className="h-4 w-4 text-volt" /> No enquiries yet
            </div>
          ) : (
            enquiries.map((q, i) => (
              <article
                key={q.id}
                data-testid={`enquiry-row-${i + 1}`}
                className="grid grid-cols-1 gap-4 border-b border-r border-line p-6 transition-colors duration-300 hover:bg-ash/60 md:grid-cols-12 md:p-8"
              >
                <div className="md:col-span-3">
                  <p className="font-display text-lg font-medium tracking-tight">{q.name}</p>
                  {q.company && <p className="mt-1 text-sm text-smoke">{q.company}</p>}
                  <span
                    className={`mt-3 inline-block border px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.2em] ${
                      q.source === "calculator"
                        ? "border-cyber/50 text-cyber"
                        : "border-volt/50 text-volt"
                    }`}
                  >
                    {q.source === "calculator" ? "Calculator Lead" : "Contact Form"}
                  </span>
                </div>
                <div className="font-mono text-xs leading-relaxed text-smoke md:col-span-3">
                  <p className="text-white">{q.email}</p>
                  {q.phone && <p className="mt-1">{q.phone}</p>}
                  <p className="mt-3 text-[10px] uppercase tracking-[0.15em]">
                    {formatDate(q.created_at)}
                  </p>
                  <p className="mt-1 text-[10px] uppercase tracking-[0.15em]">
                    Email sent: {q.email_sent ? "Yes" : "No"}
                  </p>
                </div>
                <p className="whitespace-pre-wrap text-sm leading-relaxed text-smoke md:col-span-6">
                  {q.message}
                </p>
              </article>
            ))
          )}
        </div>
      </main>
    </div>
  );
}
